import type { ApprovalStatus, ToolResult } from "@ark-bot/protocol";
import { useEffect, useRef } from "react";
import Markdown from "react-markdown";

import { api } from "../api.js";
import { useText } from "../i18n.js";
import { summarizeArgs, type AssistantPart, type ChatItem, type ChildSessionView, type ToolCallView } from "../state.js";
import { TRANSCRIPT } from "../text/transcript.js";

const APPROVAL_LABEL: Record<ApprovalStatus, (t: (typeof TRANSCRIPT)["en"]) => string> = { pending: (t) => t.approvalPending, approved: (t) => t.approvalApproved, denied: (t) => t.approvalDenied, expired: (t) => t.approvalExpired };

function ResultParts({ result }: { readonly result: ToolResult }): React.JSX.Element {
  const t = useText(TRANSCRIPT);
  return (
    <div className={`tool-result${result.isError === true ? " is-error" : ""}`}>
      {result.parts.map((part, index) =>
        part.type === "text" ? (
          <pre key={index}>{part.text}</pre>
        ) : (
          <img key={index} src={api.blobUrl(part.blobId)} alt={t.result(part.mimeType)} />
        ),
      )}
    </div>
  );
}

/** A subagent's session, nested under the task call that started it (M5). */
function ChildSession({ child }: { readonly child: ChildSessionView }): React.JSX.Element {
  const t = useText(TRANSCRIPT);
  const state = child.finished ? (child.status ?? "completed") : "running";
  return (
    <div className={`subagent subagent-${state}`}>
      <div className="subagent-head">
        <span className="subagent-name">{child.subagent}</span>
        {child.title != null ? <span className="subagent-title">{child.title}</span> : null}
        <span className="subagent-state">{t[state]}</span>
      </div>
      {child.parts.map((part, index) => (
        <Part key={part.kind === "tool" ? part.callId : index} part={part} />
      ))}
      {child.parts.length === 0 && !child.finished ? <div className="muted">{t.starting}</div> : null}
    </div>
  );
}

function ToolCall({ tool }: { readonly tool: ToolCallView }): React.JSX.Element {
  const running = tool.result == null;
  const failed = tool.result?.isError === true;
  return (
    <details className={`tool${running ? " running" : ""}${failed ? " failed" : ""}`} open={tool.child != null && running ? true : undefined}>
      <summary>
        <span className="tool-state">{running ? "⋯" : failed ? "✗" : "✓"}</span>
        <code>{summarizeArgs(tool.name, tool.args)}</code>
      </summary>
      <div className="tool-body">
        <div className="tool-args">
          <pre>{JSON.stringify(tool.args, null, 2)}</pre>
        </div>
        {tool.child != null ? <ChildSession child={tool.child} /> : null}
        {tool.progress.length > 0 && running ? <pre className="tool-progress">{tool.progress}</pre> : null}
        {tool.result != null ? <ResultParts result={tool.result} /> : null}
      </div>
    </details>
  );
}

function Part({ part }: { readonly part: AssistantPart }): React.JSX.Element {
  const t = useText(TRANSCRIPT);
  switch (part.kind) {
    case "text":
      return (
        <div className="assistant-text">
          <Markdown>{part.text}</Markdown>
        </div>
      );
    case "reasoning":
      return (
        <details className="reasoning">
          <summary>{t.reasoning}</summary>
          <div>{part.text}</div>
        </details>
      );
    case "tool":
      return <ToolCall tool={part} />;
  }
}

function Item({ item }: { readonly item: ChatItem }): React.JSX.Element {
  const t = useText(TRANSCRIPT);
  switch (item.kind) {
    case "user":
      return (
        <div className={`item user${item.hidden === true ? " hidden-prompt" : ""}`}>
          {item.hidden === true ? <div className="notification-label">{t.hiddenPrompt}</div> : null}
          {item.attachments.map((attachment) => (
            <img key={attachment.blobId} src={api.blobUrl(attachment.blobId)} alt={t.attachment} className="user-image" />
          ))}
          {item.text.length > 0 ? <div className="user-text">{item.text}</div> : null}
        </div>
      );
    case "assistant":
      return (
        <div className={`item assistant${item.finished ? "" : " streaming"}`}>
          {item.parts.map((part, index) => (
            <Part key={part.kind === "tool" ? part.callId : index} part={part} />
          ))}
          {item.finished && item.usage != null ? (
            <div className="usage">{t.usage(item.usage.inputTokens, item.usage.outputTokens, item.usage.reasoningTokens)}</div>
          ) : null}
        </div>
      );
    case "approval":
      return (
        <div className={`item approval-note approval-${item.status}`}>
          <div className="notification-label">{APPROVAL_LABEL[item.status](t)}</div>
          <div>
            {item.tool}: {item.summary}
          </div>
          <div className="muted">{item.reason}</div>
        </div>
      );
    case "condensed":
      return (
        <div className="item condensed">
          <div className="notification-label">{t.condensedLabel}</div>
          <div className="muted">{t.condensed(item.condensedEntries, item.epoch)}</div>
        </div>
      );
    case "error":
      return <div className={`item error${item.retryable ? " retryable" : ""}`}>{item.message}</div>;
  }
}

/**
 * The full conversation: what the model was given (user messages, hidden
 * prompts) and everything it did (private text, reasoning, tool calls,
 * nested subagents). The prototype's outline panel; the chat itself is Chat.
 */
export function Transcript({ items, busy }: { readonly items: readonly ChatItem[]; readonly busy: boolean }): React.JSX.Element {
  const container = useRef<HTMLDivElement>(null);
  const pinned = useRef(true);
  const t = useText(TRANSCRIPT);

  useEffect(() => {
    const element = container.current;
    if (element != null && pinned.current) element.scrollTop = element.scrollHeight;
  }, [items, busy]);

  return (
    <div
      className="transcript"
      ref={container}
      onScroll={(event) => {
        const element = event.currentTarget;
        pinned.current = element.scrollHeight - element.scrollTop - element.clientHeight < 40;
      }}
    >
      {items.length === 0 ? <div className="empty">{t.noActivity}</div> : null}
      {items.map((item, index) => (
        <Item key={index} item={item} />
      ))}
    </div>
  );
}
